import { useQuery } from '@tanstack/react-query';
import { Drawer, Typography } from 'antd';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { movementsApi } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { ListMovementsParams, MovementKind } from '../../api/types';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../components/PageStates';
import { formatKopeks } from '../../lib/money';
import { MovementRowCard } from './MovementRowCard';

/** A tapped grid cell: one store, one day. */
export interface MovementCellRef {
  date: string;
  storeId: string;
  storeName: string;
}

/**
 * What is behind a «Перемещения» grid cell: every movement that left the
 * store that day (same filter the grid summed: source store, action day,
 * kind, undone excluded), each linking to its накладная. Always mounted;
 * `detail === null` closes it.
 */
export function MovementsDayDrawer({
  detail,
  kind,
  onClose,
}: {
  detail: MovementCellRef | null;
  kind: MovementKind | 'ALL';
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const params: ListMovementsParams = {
    from: detail?.date ?? '',
    to: detail?.date ?? '',
    sourceStoreId: detail?.storeId ?? '',
    ...(kind === 'ALL' ? {} : { kind }),
    pageSize: 200,
  };
  const query = useQuery({
    queryKey: queryKeys.movements(params as Record<string, unknown>),
    queryFn: () => movementsApi.list(params),
    enabled: detail !== null,
  });
  const items = query.data?.items ?? [];
  const daySum = items.reduce((acc, r) => acc + (r.item.buySumKopeks ?? 0), 0);

  return (
    <Drawer
      open={detail !== null}
      onClose={onClose}
      title={detail ? `${detail.storeName} · ${dayjs(detail.date).format('D MMMM YYYY')}` : ''}
      placement="bottom"
      height="80%"
      styles={{ body: { display: 'grid', gap: 12, alignContent: 'start' } }}
    >
      {query.isLoading ? (
        <LoadingBlock />
      ) : query.isError || !query.data ? (
        <ErrorBlock onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <EmptyBlock description={t('movements.empty')} />
      ) : (
        <div>
          {items.map((row) => (
            <MovementRowCard key={row.id} row={row} />
          ))}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '12px 4px 0',
              borderTop: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            <Typography.Text strong>{t('movements.dayTotal')}</Typography.Text>
            <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>
              {formatKopeks(daySum)}
            </Typography.Text>
          </div>
        </div>
      )}
    </Drawer>
  );
}
